import { useEffect } from 'react';
import { validate as uuidValidate } from 'uuid';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApplicationFormPageEnum } from './types';
import { Divider } from 'components/common';
import {
  addRouteToApplicationRoutes,
  getLasestApplicationProgressRoute,
  parseApplicationRoutesAndSaveToStorage,
  removeRoutesFromApplicationRoutes,
} from 'utils/localStorage';
import {
  AdditionalNotes,
  AddressWrapper,
  ApplicationType,
  BenefitsAndCredits,
  BonusAndCommision,
  CardsWrapper,
  ContactDetails,
  Criteria,
  Dependants,
  Employment,
  EmploymentTenure,
  FullName,
  HouseholdOutgoings,
  HouseholdSpendings,
  Income,
  IndustryAndRole,
  InReview,
  IntermediaryFees,
  Introducer,
  Introduction,
  LoanRepayments,
  OtherIncome,
  PartnerAddressHistory,
  PersonalDetails,
  PreviousName,
  Purchase,
  Refinancing,
  Region,
  Relationship,
  Residency,
  ReviewAndSubmit,
  SecondJob,
  StudentLoansWrapper,
  Submission,
  TravelExpenses,
} from 'pages/ApplicationFormPage';
import { PageURL } from 'router/pageURL';

import { useApplicationInProgressById } from 'queries/applications/useApplicationInProgressById';

export const ApplicationFormPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formApplicantNames = searchParams.get('applicantNames')?.split(',');
  const activeApplicant = parseInt(searchParams.get('activeApplicant') || '');
  const activeApplicantName = formApplicantNames?.[activeApplicant - 1] || '';
  const { id = '' } = useParams();
  const formSection = searchParams.get('section') as ApplicationFormPageEnum;
  const { data: applicationInProgress } = useApplicationInProgressById(id);

  useEffect(() => {
    const latestRoute = getLasestApplicationProgressRoute();

    navigate(latestRoute);

    return removeRoutesFromApplicationRoutes;
  }, []);

  useEffect(() => {
    if (applicationInProgress) parseApplicationRoutesAndSaveToStorage(applicationInProgress);

    const latestRoute = getLasestApplicationProgressRoute();

    if (!uuidValidate(id) || !latestRoute) navigate(PageURL.APPLICATIONS);
    if (!formSection || !ApplicationFormPageEnum[formSection]) navigate(latestRoute);

    addRouteToApplicationRoutes();
  }, [id, formSection, applicationInProgress]);

  const ActiveFormPage = () => {
    switch (formSection) {
      case ApplicationFormPageEnum.CRITERIA:
        return <Criteria />;
      case ApplicationFormPageEnum.SUBMISSION:
        return <Submission />;
      case ApplicationFormPageEnum.INTERMEDIARY_FEES:
        return <IntermediaryFees />;
      case ApplicationFormPageEnum.INTRODUCER:
        return <Introducer />;
      case ApplicationFormPageEnum.APPLICATION_TYPE:
        return <ApplicationType />;
      case ApplicationFormPageEnum.RELATIONSHIP:
        return <Relationship />;
      case ApplicationFormPageEnum.FULL_NAME:
        return <FullName />;
      case ApplicationFormPageEnum.PREVIOUS_NAME:
        return <PreviousName />;
      case ApplicationFormPageEnum.PERSONAL_DETAILS:
        return <PersonalDetails />;
      case ApplicationFormPageEnum.CONTACT_DETAILS:
        return <ContactDetails />;
      case ApplicationFormPageEnum.RESIDENCY:
        return <Residency />;
      case ApplicationFormPageEnum.ADDRESS_HISTORY:
      case ApplicationFormPageEnum.ADD_ADDRESS:
        return <AddressWrapper />;
      case ApplicationFormPageEnum.PARTNER_ADDRESS_HISTORY:
        return <PartnerAddressHistory />;
      case ApplicationFormPageEnum.INTRODUCTION:
        return <Introduction />;
      case ApplicationFormPageEnum.PURCHASE:
        return <Purchase />;
      case ApplicationFormPageEnum.REFINANCING:
        return <Refinancing />;
      case ApplicationFormPageEnum.REGION:
        return <Region />;
      case ApplicationFormPageEnum.EMPLOYMENT:
        return <Employment />;
      case ApplicationFormPageEnum.INDUSTRY_AND_ROLE:
        return <IndustryAndRole />;
      case ApplicationFormPageEnum.EMPLOYMENT_TENURE:
        return <EmploymentTenure />;
      case ApplicationFormPageEnum.INCOME:
        return <Income />;
      case ApplicationFormPageEnum.BONUS_AND_COMMISSIONS:
        return <BonusAndCommision />;
      case ApplicationFormPageEnum.SECOND_JOB:
        return <SecondJob />;
      case ApplicationFormPageEnum.OTHER_INCOME:
        return <OtherIncome />;
      case ApplicationFormPageEnum.BENEFITS_AND_CREDITS:
        return <BenefitsAndCredits />;
      case ApplicationFormPageEnum.DEPENDANTS:
        return <Dependants />;
      case ApplicationFormPageEnum.HOUSEHOLD_SPENDINGS:
        return <HouseholdSpendings />;
      case ApplicationFormPageEnum.HOUSEHOLD_OUTGOINGS:
        return <HouseholdOutgoings />;
      case ApplicationFormPageEnum.TRAVEL_EXPENSES:
        return <TravelExpenses />;
      case ApplicationFormPageEnum.LOAN_REPAYMENTS:
        return <LoanRepayments />;
      case ApplicationFormPageEnum.STUDENT_LOANS:
      case ApplicationFormPageEnum.ADD_STUDENT_LOAN:
        return <StudentLoansWrapper />;
      case ApplicationFormPageEnum.ADDITIONAL_NOTES:
        return <AdditionalNotes />;
      case ApplicationFormPageEnum.CARDS:
      case ApplicationFormPageEnum.ADD_CARD:
        return <CardsWrapper />;
      case ApplicationFormPageEnum.REVIEW_AND_SUBMIT:
        return <ReviewAndSubmit />;
      case ApplicationFormPageEnum.IN_REVIEW:
        return <InReview />;
      default:
        return null;
    }
  };

  return (
    <div className={'mx-auto max-w-screen-md'}>
      {formSection && (
        <h2 className={'mb-0'}>
          <FormattedMessage
            id={`applicationForm.${formSection}.label`}
            values={{ name: activeApplicantName ? `${activeApplicantName}'s` : `Applicant ${activeApplicant}'s` }}
          />
        </h2>
      )}
      <Divider className={'my-4'} />
      <ActiveFormPage />
    </div>
  );
};
